import React, { useMemo, useState } from "react";
import "./LoanSimulatorGirona.css";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  Bookmark,
  Briefcase,
  Building,
  Calendar,
  CreditCard,
  Download,
  Euro,
  Hash,
  Home,
  KeyRound,
  User,
  Users,
} from "lucide-react";
import {
  SimulatorHeader,
  ReuseCard,
  FieldWithIcon,
  FieldLabel,
} from "../components/SimulatorParts";
import useSimulatorFormat from "../components/useSimulatorFormat";
import { downloadSimulationWorkbook } from "../utils/simulationExcel";
import {
  asArray,
  asText,
  clampNumber,
  readSimulationWorkbook,
} from "../utils/simulationExcelImport";
import {
  EMPLOYMENT_SITUATIONS,
  GIRONA_LOAN_PARAMS,
  MAX_AGE_AT_END,
  MAX_TERM_YEARS,
  MIN_AGE,
  MIN_TERM_YEARS,
  maxTermForAge,
  simulateLoanGirona,
} from "./LoanSimulatorGironaCalc";

const NEW_BORROWER = { employment: "permanent", monthlyIncome: "", paymentsPerYear: 14, otherLoans: "" };

function BorrowerFields({ index, value, onChange, t }) {
  const update = (field, parse = (v) => v) => (e) => onChange({ ...value, [field]: parse(e.target.value) });

  return (
    <div className="simulator__subcard">
      <span className="simulator__subcard-title">
        <User size={14} /> {t("borrower", { n: index + 1 })}
      </span>
      <div className="simulator__subgrid">
        <div className="simulator__field">
          <FieldLabel tip={t("tips.employment")}>{t("employment")}</FieldLabel>
          <FieldWithIcon icon={Briefcase}>
            <select value={value.employment} onChange={update("employment")}>
              {EMPLOYMENT_SITUATIONS.map((e) => (
                <option key={e} value={e}>{t(`employments.${e}`)}</option>
              ))}
            </select>
          </FieldWithIcon>
        </div>
        <div className="simulator__field">
          <FieldLabel tip={t("tips.monthly-income")}>{t("monthly-income")}</FieldLabel>
          <FieldWithIcon icon={Euro} unit="€">
            <input
              type="number"
              min="0"
              step="0.01"
              value={value.monthlyIncome}
              onChange={update("monthlyIncome")}
              placeholder="0"
            />
          </FieldWithIcon>
        </div>
        <div className="simulator__field">
          <FieldLabel tip={t("tips.payments-per-year")}>{t("payments-per-year")}</FieldLabel>
          <FieldWithIcon icon={Hash}>
            <select value={value.paymentsPerYear} onChange={update("paymentsPerYear", Number)}>
              {[12, 14].map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </FieldWithIcon>
        </div>
        <div className="simulator__field">
          <FieldLabel tip={t("tips.other-loans")}>{t("other-loans")}</FieldLabel>
          <FieldWithIcon icon={CreditCard} unit="€">
            <input
              type="number"
              min="0"
              step="0.01"
              value={value.otherLoans}
              onChange={update("otherLoans")}
              placeholder="0"
            />
          </FieldWithIcon>
        </div>
      </div>
    </div>
  );
}

function CostRow({ label, value, className = "" }) {
  return (
    <div className={`simulator__taxes-row ${className}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

export default function LoanSimulatorGirona({ onBack, onContinue }) {
  const { t, i18n } = useTranslation("loan-simulator-girona");
  const { money, percent } = useSimulatorFormat();

  const [borrowerCount, setBorrowerCount] = useState(1);
  const [borrowers, setBorrowers] = useState([NEW_BORROWER, NEW_BORROWER]);
  const [oldestAge, setOldestAge] = useState("");
  const [residence, setResidence] = useState("main");
  const [propertyType, setPropertyType] = useState("secondHand");
  const [termYears, setTermYears] = useState(30);
  const [propertyPrice, setPropertyPrice] = useState("");

  // The term is limited by the age of the oldest borrower (at most 75 − age)
  const age = Number(oldestAge);
  const ageValid = oldestAge !== "" && Number.isInteger(age) && age >= MIN_AGE;
  const termMax = ageValid ? Math.max(MIN_TERM_YEARS, maxTermForAge(age)) : MAX_TERM_YEARS;
  const term = Math.min(termYears, termMax);

  const result = useMemo(
    () =>
      simulateLoanGirona({
        borrowers: borrowers.slice(0, borrowerCount),
        oldestAge: ageValid ? age : NaN,
        residence,
        propertyType,
        termYears: term,
        propertyPrice,
      }),
    [borrowers, borrowerCount, ageValid, age, residence, propertyType, term, propertyPrice]
  );
  const ok = !result.error;

  const fixedYears = GIRONA_LOAN_PARAMS.initialFixedYears;

  const handleDownload = async () => {
    const currentInputs = {
      borrowerCount,
      borrowers:
        borrowers.slice(
          0,
          borrowerCount,
        ),
      oldestAge,
      residence,
      propertyType,
      termYears:
        term,
      propertyPrice,
    };

    const currentResult =
      simulateLoanGirona({
        borrowers:
          currentInputs.borrowers,
        oldestAge:
          ageValid
            ? age
            : NaN,
        residence,
        propertyType,
        termYears:
          term,
        propertyPrice,
      });

    if (
      !currentResult ||
      currentResult.error
    ) {
      return;
    }

    await downloadSimulationWorkbook({
      simulationType: "LOAN_CAPACITY",
      region: "GIRONA",
      language:
        i18n.resolvedLanguage ||
        i18n.language ||
        "en",
      calculatorVersion:
        GIRONA_LOAN_PARAMS.version,
      inputs:
        currentInputs,
      results:
        currentResult,
    });
  };

  const errorMessage = {
    income: t("empty-result"),
    age: t("empty-result"),
    term: t("error-age-term", { max: MAX_AGE_AT_END - MIN_TERM_YEARS }),
    noCapacity: t("error-no-capacity"),
  }[result.error];

  const handleApplyWorkbook = async (
    file,
  ) => {
    const { inputs } =
      await readSimulationWorkbook(
        file,
        {
          expectedSimulationType:
            "LOAN_CAPACITY",
          expectedRegion:
            "GIRONA",
        },
      );

    const importedBorrowers =
      asArray(
        inputs.borrowers,
      );

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "borrowerCount",
      )
    ) {
      setBorrowerCount(
        clampNumber(
          inputs.borrowerCount,
          1,
          2,
          1,
        ),
      );
    }

    if (
      importedBorrowers.length
    ) {
      setBorrowers(
        [0, 1].map(
          (index) => {
            const imported =
              importedBorrowers[
                index
              ] || {};

            return {
              ...NEW_BORROWER,
              employment:
                imported.employment ||
                NEW_BORROWER.employment,
              monthlyIncome:
                asText(
                  imported.monthlyIncome,
                ),
              paymentsPerYear:
                [12, 14].includes(
                  Number(
                    imported.paymentsPerYear,
                  ),
                )
                  ? Number(
                      imported.paymentsPerYear,
                    )
                  : 14,
              otherLoans:
                asText(
                  imported.otherLoans,
                ),
            };
          },
        ),
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "oldestAge",
      )
    ) {
      setOldestAge(
        asText(
          inputs.oldestAge,
        ),
      );
    }

    if (
      ["main", "second"].includes(
        inputs.residence,
      )
    ) {
      setResidence(
        inputs.residence,
      );
    }

    if (
      ["new", "secondHand"].includes(
        inputs.propertyType,
      )
    ) {
      setPropertyType(
        inputs.propertyType,
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "termYears",
      )
    ) {
      setTermYears(
        clampNumber(
          inputs.termYears,
          MIN_TERM_YEARS,
          MAX_TERM_YEARS,
          30,
        ),
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "propertyPrice",
      )
    ) {
      setPropertyPrice(
        asText(
          inputs.propertyPrice,
        ),
      );
    }
  };

  return (
    <div className="simulator">
      {/* ---------- LEFT COLUMN ---------- */}
      <div className="simulator__left-col">
        <SimulatorHeader eyebrow={t("eyebrow")} title={t("title")} subtitle={t("subtitle")} />

        <ReuseCard
          title={t("reuse-title")}
          description={t("reuse-desc")}
          browseLabel={t("browse")}
          applyLabel={t("upload-apply")}
          onApply={handleApplyWorkbook}
        />

        <div className="simulator__card simulator__form-card">
          {/* Household */}
          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Users size={16} />
              <span>{t("household")}</span>
            </div>
            <button type="button" className="simulator__btn-outline simulator__btn-outline--sm">
              <Bookmark size={14} /> {t("load-saved")}
            </button>
          </div>

          <div className="simulator__grid">
            <div className="simulator__field">
              <FieldLabel tip={t("tips.borrowers-count")}>{t("borrowers-count")}</FieldLabel>
              <FieldWithIcon icon={Users}>
                <select value={borrowerCount} onChange={(e) => setBorrowerCount(Number(e.target.value))}>
                  <option value={1}>{t("borrowers-one")}</option>
                  <option value={2}>{t("borrowers-two")}</option>
                </select>
              </FieldWithIcon>
            </div>
            <div className="simulator__field">
              <FieldLabel tip={t("tips.oldest-age")}>{t("oldest-age")}</FieldLabel>
              <FieldWithIcon icon={Calendar}>
                <input
                  type="number"
                  min={MIN_AGE}
                  step="1"
                  value={oldestAge}
                  onChange={(e) => setOldestAge(e.target.value)}
                  placeholder="0"
                />
              </FieldWithIcon>
            </div>
          </div>

          <div className="simulator__subcards">
            {borrowers.slice(0, borrowerCount).map((borrower, index) => (
              <BorrowerFields
                key={index}
                index={index}
                value={borrower}
                onChange={(value) =>
                  setBorrowers((prev) => prev.map((b, i) => (i === index ? value : b)))
                }
                t={t}
              />
            ))}
          </div>

          {/* Purchase */}
          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Home size={16} />
              <span>{t("purchase")}</span>
            </div>
          </div>

          <div className="simulator__grid">
            <div className="simulator__field">
              <FieldLabel tip={t("tips.residence")}>{t("residence")}</FieldLabel>
              <FieldWithIcon icon={KeyRound}>
                <select value={residence} onChange={(e) => setResidence(e.target.value)}>
                  <option value="main">{t("residences.main")}</option>
                  <option value="second">{t("residences.second")}</option>
                </select>
              </FieldWithIcon>
            </div>
            <div className="simulator__field">
              <FieldLabel tip={t("tips.property-type")}>{t("property-type")}</FieldLabel>
              <FieldWithIcon icon={Building}>
                <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
                  <option value="secondHand">{t("property-types.secondHand")}</option>
                  <option value="newBuild">{t("property-types.newBuild")}</option>
                </select>
              </FieldWithIcon>
            </div>

            <div className="simulator__field simulator__field--slider simulator__field--wide">
              <FieldLabel tip={t("tips.term")}>{t("term")}</FieldLabel>
              <div className="simulator__slider-row">
                <input
                  type="range"
                  min={MIN_TERM_YEARS}
                  max={termMax}
                  value={term}
                  onChange={(e) => setTermYears(Number(e.target.value))}
                />
                <span className="simulator__slider-value">{t("years", { count: term })}</span>
              </div>
              <div className="simulator__slider-limits">
                <span>{MIN_TERM_YEARS}</span>
                <span>{termMax}</span>
              </div>
            </div>

            <div className="simulator__field simulator__field--wide">
              <FieldLabel tip={t("tips.property-price")}>{t("property-price")}</FieldLabel>
              <FieldWithIcon icon={Euro} unit="€">
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={propertyPrice}
                  onChange={(e) => setPropertyPrice(e.target.value)}
                  placeholder={t("property-price-placeholder")}
                />
              </FieldWithIcon>
            </div>
          </div>
          <p className="simulator__hint simulator__hint--after-grid">{t("property-price-hint")}</p>

          <div className="simulator__footer">
            <button type="button" className="simulator__btn-back" onClick={onBack}>
              <ArrowLeft size={16} /> {t("back")}
            </button>
            <button type="button" className="simulator__btn-continue" onClick={onContinue}>
              {t("save-continue")} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ---------- RESULT ---------- */}
      <div className="simulator__card simulator__result-card">
        <div className="simulator__tags">
          <span className="simulator__tag">{t("tag-catalonia")}</span>
          <span className="simulator__tag">{t("tag-mixed", { years: fixedYears })}</span>
          {ok && <span className="simulator__tag">{t("tag-tin", { rate: percent(result.initialRate) })}</span>}
          <span className="simulator__tag simulator__tag--badge">{t("tag-indicative")}</span>
          {ok && <span className="simulator__tag">{t("tag-tae", { rate: percent(result.tae) })}</span>}
        </div>

        <label className="simulator__result-label">
          {ok && result.withPrice ? t("loan-amount") : t("max-price")}
        </label>
        <span className="simulator__result-value">
          {ok ? money(result.withPrice ? result.loanAmount : result.propertyPrice) : "— €"}
        </span>

        {!ok ? (
          result.error === "income" || result.error === "age" ? (
            <p className="simulator__empty-result">{errorMessage}</p>
          ) : (
            <p className="simulator__note simulator__note--warning">{errorMessage}</p>
          )
        ) : (
          <>
            <div className="simulator__mini-cards">
              <div className="simulator__mini-card">
                <span className="simulator__mini-label">{t("savings-needed")}</span>
                <span className="simulator__mini-value">{money(result.savings)}</span>
              </div>
              <div className="simulator__mini-card">
                <span className="simulator__mini-label">
                  {result.instalmentReview === null
                    ? t("monthly-instalment")
                    : t("instalment-initial", { years: fixedYears })}
                </span>
                <span className="simulator__mini-value">{money(result.instalmentInitial)}</span>
              </div>
            </div>

            {result.ratioWarning && (
              <p className="simulator__note simulator__note--warning">{t("ratio-warning")}</p>
            )}

            <div className="simulator__rows">
              {result.withPrice ? (
                <div className="simulator__row">
                  <span>{t("property-price-row")}</span>
                  <span>{money(result.propertyPrice)}</span>
                </div>
              ) : (
                <div className="simulator__row">
                  <span>{t("loan-amount")}</span>
                  <span>{money(result.loanAmount)}</span>
                </div>
              )}
              <div className="simulator__row">
                <span>{t("ltv")}</span>
                <span>{percent(result.ltv)}</span>
              </div>
              {result.instalmentReview !== null && (
                <div className="simulator__row">
                  <span>{t("instalment-review")}</span>
                  <span>{money(result.instalmentReview)}</span>
                </div>
              )}
              <div className="simulator__row">
                <span>{t("initial-rate")}</span>
                <span>{percent(result.initialRate)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("review-rate", { years: fixedYears })}</span>
                <span>{percent(result.reviewRate)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("tae")}</span>
                <span>{percent(result.tae)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("ratio")}</span>
                <span>{percent(result.ratio)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("loan-term")}</span>
                <span>{t("term-value", { years: result.termYears, months: result.termMonths })}</span>
              </div>
            </div>

            <div className="simulator__taxes-box">
              <span className="simulator__taxes-title">{t("taxes-title")}</span>
              <CostRow label={t("appraisal")} value={money(result.breakdown.appraisal)} />
              <CostRow
                label={t(result.breakdown.purchaseTaxKind, { rate: percent(result.breakdown.purchaseTaxRate, 1) })}
                value={money(result.breakdown.purchaseTax)}
              />
              <CostRow label={t("notary")} value={money(result.breakdown.notary)} />
              <CostRow
                label={t("commission", { rate: percent(GIRONA_LOAN_PARAMS.bankCommission, 1) })}
                value={money(result.breakdown.commission)}
              />
              <CostRow
                label={t("taxes-total")}
                value={money(result.taxesAndExpenses)}
                className="simulator__taxes-row--total"
              />
              <p className="loan-gi__rounding-note">{t("rounding-note")}</p>
            </div>

            <p className="simulator__note">{t("calculator-note")}</p>
          </>
        )}

        <p className="simulator__disclaimer">
          {t("disclaimer")}
          <br />
          {GIRONA_LOAN_PARAMS.version}
        </p>

        <button
          type="button"
          className="simulator__btn-download"
          onClick={handleDownload}
          disabled={!ok}
        >
          <Download size={16} /> {t("download")}
        </button>
      </div>
    </div>
  );
}
